import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Phone, Users, Pencil, UserPlus, X, Trash2, Copy } from "lucide-react";

export function ProfilePanel() {
  const { user, refreshMe } = useAuth();
  const isLicencie = user?.role === "licencie";
  const isCollaborator = isLicencie && !!user?.parent_licencie_id;
  const canManageAgency = isLicencie && !isCollaborator;

  // Profile state
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    prenom: user?.prenom || "",
    nom: user?.nom || "",
    responsable: user?.responsable || "",
    telephone_agence: user?.telephone_agence || "",
  });
  const [saving, setSaving] = useState(false);

  // Parrainage state
  const [parrainageOn, setParrainageOn] = useState(Boolean(user?.parrainage_enabled));
  const [parrainageAmount, setParrainageAmount] = useState(user?.parrainage_amount || 10);
  const [savingP, setSavingP] = useState(false);

  // Collaborators state
  const [collabs, setCollabs] = useState([]);
  const [collabsLoading, setCollabsLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", prenom: "", nom: "" });
  const [inviting, setInviting] = useState(false);
  const [lastDevLink, setLastDevLink] = useState(null);

  const loadCollabs = useCallback(async () => {
    if (!canManageAgency) return;
    setCollabsLoading(true);
    try {
      const { data } = await api.get("/me/collaborators");
      setCollabs(data || []);
    } catch (err) { /* ignore */ }
    finally { setCollabsLoading(false); }
  }, [canManageAgency]);

  useEffect(() => { loadCollabs(); }, [loadCollabs]);

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/profile", {
        prenom: form.prenom.trim() || null,
        nom: form.nom.trim() || null,
        responsable: form.responsable.trim() || null,
        telephone_agence: form.telephone_agence.trim() || null,
      });
      if (refreshMe) await refreshMe();
      toast.success("Fiche mise à jour");
      setEdit(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {
    setForm({
      prenom: user?.prenom || "",
      nom: user?.nom || "",
      responsable: user?.responsable || "",
      telephone_agence: user?.telephone_agence || "",
    });
    setEdit(false);
  };

  const onSaveParrainage = async (e) => {
    e.preventDefault();
    setSavingP(true);
    try {
      await api.patch("/me/profile", {
        parrainage_enabled: parrainageOn,
        parrainage_amount: Math.max(1, Math.min(100, Number(parrainageAmount) || 10)),
      });
      if (refreshMe) await refreshMe();
      toast.success(parrainageOn ? "Booster parrainage activé" : "Booster parrainage désactivé");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingP(false); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/invitations", {
        email: invite.email.trim(),
        prenom: invite.prenom.trim(),
        nom: invite.nom.trim(),
        role: "licencie",
      });
      toast.success("Invitation envoyée à " + data.email);
      if (data.dev_link) setLastDevLink(data.dev_link);
      setInvite({ email: "", prenom: "", nom: "" });
      setInviteOpen(false);
      loadCollabs();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setInviting(false); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Désactiver le collaborateur ${u.prenom} ${u.nom} ?`)) return;
    try {
      await api.delete(`/me/collaborators/${u.id}`);
      toast.success("Collaborateur désactivé");
      loadCollabs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  return (
    <div className="space-y-6">
      {/* Mon agence */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="profile-panel">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#191D1E]">Mon agence</h2>
            <p className="text-sm text-[#687175] mt-1 max-w-xl">
              Infos visibles sur les supports marketing et la landing publique de votre zone.
            </p>
          </div>
          {!edit && !isCollaborator && (
            <Button variant="outline" onClick={() => setEdit(true)} data-testid="profile-edit-btn" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier ma fiche
            </Button>
          )}
        </div>

        {!edit ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div><Label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold">Email</Label><div className="text-sm text-[#191D1E] mt-1">{user?.email}</div></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold">Zone</Label><div className="text-sm text-[#191D1E] mt-1">{user?.zone || "—"}</div></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold">Nom</Label><div className="text-sm text-[#191D1E] mt-1">{user?.prenom} {user?.nom}</div></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold">Responsable</Label><div className="text-sm text-[#191D1E] mt-1">{user?.responsable || "—"}</div></div>
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold flex items-center gap-1.5"><Phone className="w-3 h-3" /> Téléphone</Label><div className="text-sm text-[#191D1E] mt-1">{user?.telephone_agence || <span className="text-[#939BA0] italic">Hérité du Siège</span>}</div></div>
            {isCollaborator && (
              <div className="sm:col-span-2 mt-2 px-3 py-2 rounded-lg bg-[#F5F6F9] border border-[#E5E7EB] text-xs text-[#687175]">
                Vous êtes <strong className="text-[#191D1E]">collaborateur·trice</strong> de l'agence. La fiche est gérée par le licencié principal.
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={onSaveProfile} className="space-y-4 mt-5 max-w-2xl" data-testid="profile-edit-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Prénom</Label><Input value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} data-testid="profile-edit-prenom" className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Nom</Label><Input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} data-testid="profile-edit-nom" className="rounded-xl" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold">Responsable de l'agence</Label><Input value={form.responsable} onChange={(e) => setForm((f) => ({ ...f, responsable: e.target.value }))} data-testid="profile-edit-responsable" className="rounded-xl" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold">Téléphone de l'agence</Label><Input value={form.telephone_agence} onChange={(e) => setForm((f) => ({ ...f, telephone_agence: e.target.value }))} placeholder="03 27 12 34 56" data-testid="profile-edit-tel" className="rounded-xl" /></div>
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl" data-testid="profile-save">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
              <Button type="button" variant="outline" onClick={cancelEdit} className="rounded-xl border-[#E5E7EB]" data-testid="profile-cancel">Annuler</Button>
            </div>
          </form>
        )}
      </div>

      {/* Booster parrainage */}
      {isLicencie && !isCollaborator && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="parrainage-panel">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[rgba(57,125,255,0.08)] flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[#397DFF]" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-[#191D1E]">Booster parrainage</h2>
              <p className="text-sm text-[#687175] mt-1 max-w-xl">
                Pour chaque filleul converti, ce montant est ajouté au bon d'achat du parrain. <strong className="text-[#191D1E]">Financé par votre agence.</strong>
              </p>
            </div>
          </div>
          <form onSubmit={onSaveParrainage} className="space-y-5 mt-5 max-w-md">
            <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-[#F5F6F9] border border-[#E5E7EB]">
              <div>
                <div className="text-sm font-semibold text-[#191D1E]">Activer le booster</div>
                <div className="text-xs text-[#687175] mt-0.5">{parrainageOn ? "Activé sur toute votre zone." : "Désactivé."}</div>
              </div>
              <Switch checked={parrainageOn} onCheckedChange={setParrainageOn} data-testid="parrainage-toggle" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Montant TTC par filleul converti</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" min={1} max={100} value={parrainageAmount} onChange={(e) => setParrainageAmount(e.target.value)} disabled={!parrainageOn} className="rounded-xl w-32" data-testid="parrainage-amount" />
                <span className="text-sm text-[#687175]">€ TTC</span>
              </div>
              <p className="text-xs text-[#687175] mt-1.5">Conseillé : 10 €. Min 1 € — max 100 €.</p>
            </div>
            <Button type="submit" disabled={savingP} className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl" data-testid="parrainage-save">{savingP ? "Enregistrement…" : "Enregistrer"}</Button>
          </form>
        </div>
      )}

      {/* Collaborateurs */}
      {canManageAgency && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="collabs-panel">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[rgba(57,125,255,0.08)] flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-[#397DFF]" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-[#191D1E]">Collaborateurs de mon agence</h2>
                <p className="text-sm text-[#687175] mt-1 max-w-xl">
                  Invitez des membres de votre équipe à partager votre périmètre (mêmes commerçants, mêmes leads).
                </p>
              </div>
            </div>
            <Button onClick={() => setInviteOpen(true)} data-testid="collab-invite-open" className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl"><UserPlus className="w-4 h-4 mr-1.5" /> Inviter</Button>
          </div>

          {collabsLoading ? (
            <div className="py-6 text-sm text-[#939BA0]">Chargement…</div>
          ) : collabs.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#939BA0] italic" data-testid="collabs-empty">Aucun collaborateur — vous êtes seul·e sur cette agence pour l'instant.</div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]" data-testid="collabs-list">
              {collabs.map((u) => (
                <li key={u.id} className="py-3 flex items-center justify-between" data-testid={`collab-row-${u.email}`}>
                  <div>
                    <div className="text-sm font-semibold text-[#191D1E]">{u.prenom} {u.nom}</div>
                    <div className="text-xs text-[#687175]">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={u.status === "active" ? "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0] border" : "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA] border"}>
                      {u.status === "active" ? "Actif" : "Inactif"}
                    </Badge>
                    {u.status === "active" && (
                      <Button variant="outline" size="sm" onClick={() => deactivate(u)} data-testid={`collab-deactivate-${u.email}`} className="text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2] rounded-lg">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {lastDevLink && (
            <div className="mt-5 rounded-xl bg-[#FFF7E6] border border-[#FDE68A] p-4" data-testid="collab-dev-link">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#92400E] mb-2">Mode dev — lien à transmettre</div>
              <div className="text-xs text-[#191D1E] break-all bg-white rounded-lg p-2 border border-[#FDE68A]">{lastDevLink}</div>
              <Button type="button" onClick={() => copyLink(lastDevLink)} className="mt-2 h-9 bg-[#397DFF] hover:bg-[#2860D8] text-white text-sm rounded-lg"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copier</Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="collab-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Inviter un collaborateur</DialogTitle>
            <DialogDescription>Cette personne partagera votre zone <strong className="text-[#191D1E]">{user?.zone}</strong>.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Prénom</Label><Input value={invite.prenom} onChange={(e) => setInvite((i) => ({ ...i, prenom: e.target.value }))} required data-testid="collab-invite-prenom" className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold">Nom</Label><Input value={invite.nom} onChange={(e) => setInvite((i) => ({ ...i, nom: e.target.value }))} required data-testid="collab-invite-nom" className="rounded-xl" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold">Email</Label><Input type="email" value={invite.email} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} required data-testid="collab-invite-email" className="rounded-xl" /></div>
            <Button type="submit" disabled={inviting} className="w-full bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl" data-testid="collab-invite-submit">
              {inviting ? "Envoi…" : "Envoyer l'invitation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProfilePanel;
